// Minimal path-based router. No deps — just history.pushState + popstate.
// Routes are matched as plain pathname strings. Not regex; not parametric.
// If we ever need URL params, we'll add them.

import { useEffect, useState, type AnchorHTMLAttributes, type MouseEvent } from 'react';

function currentPath(): string {
  if (typeof window === 'undefined') return '/';
  return window.location.pathname || '/';
}

export function useRoute(): string {
  const [path, setPath] = useState<string>(currentPath());
  useEffect(() => {
    const handler = () => setPath(currentPath());
    window.addEventListener('popstate', handler);
    window.addEventListener('gymdash:navigate', handler);
    return () => {
      window.removeEventListener('popstate', handler);
      window.removeEventListener('gymdash:navigate', handler);
    };
  }, []);
  return path;
}

export function navigate(path: string): void {
  if (window.location.pathname === path) return;
  window.history.pushState({}, '', path);
  window.dispatchEvent(new Event('gymdash:navigate'));
}

export function Link({
  to,
  onClick,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  return (
    <a
      href={to}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        // Let cmd/ctrl-click open in a new tab.
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();
        navigate(to);
        onClick?.(e);
      }}
      {...rest}
    >
      {children}
    </a>
  );
}
